import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  AccountCircle,
  Instagram,
  Facebook,
  Delete,
  Warning,
  Info,
  Schedule,
  CheckCircle,
  Cancel,
  Error,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Settings: React.FC = () => {
  // Settings component for user account management
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setError(null);
    
    try {
      const response = await fetch('/api/settings/delete-account', {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        await logout();
        navigate('/login');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete account');
      }
    } catch (err) {
      setError('An error occurred while deleting your account');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPlanDisplayName = (plan: string | undefined) => {
    switch (plan) {
      case 'free':
        return 'Free Plan';
      case 'starter':
        return 'Starter Plan';
      case 'professional':
        return 'Professional Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      default:
        return 'Free Plan';
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'active':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'trialing':
        return <Info sx={{ color: 'info.main', fontSize: 20 }} />;
      case 'inactive':
        return <Schedule sx={{ color: 'warning.main', fontSize: 20 }} />;
      case 'canceled':
        return <Cancel sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'past_due':
        return <Error sx={{ color: 'error.main', fontSize: 20 }} />;
      default:
        return <Schedule sx={{ color: 'grey.500', fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'inactive':
        return 'warning';
      case 'canceled':
        return 'error';
      case 'past_due':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDaysRemaining = (dateString: string | undefined) => {
    if (!dateString) return null;
    const now = new Date().getTime();
    const end = new Date(dateString).getTime();
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>

      <Card sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccountCircle sx={{ mr: 1 }} />
            Account Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Username" 
                    secondary={user?.username || 'Not available'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="User ID" 
                    secondary={user?.id || 'Not available'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Account Type" 
                    secondary={user?.authType || 'Standard'} 
                  />
                </ListItem>
              </List>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Created" 
                    secondary={formatDate(user?.created_at)} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Last Updated" 
                    secondary={formatDate(user?.updated_at)} 
                  />
                </ListItem>
              </List>
            </Box>
          </Box>

          {/* Subscription */}
          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            Subscription
          </Typography>

          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: 'rgba(0,0,0,0.015)',
              border: '1px solid rgba(0,0,0,0.04)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip label={getPlanDisplayName(user?.subscriptionPlan)} size="small" variant="outlined" />
                <Chip
                  size="small"
                  label={user?.subscriptionStatus || 'inactive'}
                  color={getStatusColor(user?.subscriptionStatus) as any}
                  icon={getStatusIcon(user?.subscriptionStatus)}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {user?.isTrialing && user?.trialEndDate && user?.subscriptionStatus === 'trialing' && (
                  <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>
                    Trial ends in {Math.max(0, getDaysRemaining(user?.trialEndDate) || 0)} days
                  </Typography>
                )}
                {user?.subscriptionEndDate && user?.subscriptionStatus === 'active' && !user?.isTrialing && (
                  <Typography variant="body2" color="text.secondary">
                    Renews in {Math.max(0, getDaysRemaining(user?.subscriptionEndDate) || 0)} days
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate('/app/checkout')}
                  disableElevation
                  sx={{
                    px: 1.25,
                    py: 0.25,
                    fontSize: '0.72rem',
                    minHeight: 26,
                    borderRadius: 1.25,
                    color: 'text.secondary',
                    borderColor: 'divider',
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.03)', borderColor: 'divider' }
                  }}
                >
                  {user?.subscriptionStatus === 'active' || user?.subscriptionStatus === 'trialing' ? 'Manage plan' : 'Upgrade plan'}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
              <Box sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: 'rgba(0,0,0,0.02)'
              }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {user?.isTrialing && user?.subscriptionStatus === 'trialing' ? 'Trial start date' : 'Start date'}
                </Typography>
                <Typography variant="body2">
                  {formatDate(user?.isTrialing && user?.subscriptionStatus === 'trialing' ? user?.trialStartDate : user?.subscriptionDate)}
                </Typography>
              </Box>
              <Box sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: 'rgba(0,0,0,0.02)'
              }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {user?.isTrialing && user?.subscriptionStatus === 'trialing' ? 'Trial end date' : 'End date'}
                </Typography>
                <Typography variant="body2">
                  {formatDate(user?.isTrialing && user?.subscriptionStatus === 'trialing' ? user?.trialEndDate : user?.subscriptionEndDate)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Facebook sx={{ mr: 1 }} />
            Facebook Integration
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Facebook Page Name" 
                secondary={user?.facebookPageName || 'Not connected'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Facebook Page ID" 
                secondary={user?.facebookPageId || 'Not connected'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Facebook ID" 
                secondary={user?.facebookId || 'Not connected'} 
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Instagram sx={{ mr: 1 }} />
            Instagram Integration
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Instagram Username" 
                secondary={user?.instagramUsername || 'Not connected'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Business Account ID" 
                secondary={user?.instagramBusinessId || 'Not connected'} 
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* WhatsApp Integration Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 1, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '100%', height: '100%' }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.87 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
            </Box>
            WhatsApp Integration
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="WhatsApp Business Account ID" 
                secondary={user?.whatsappBusinessAccountId || 'Not connected'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="WhatsApp Phone Number" 
                secondary={user?.whatsappPhoneNumber || 'Not connected'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Status" 
                secondary={user?.whatsappAccessToken ? 'Connected' : 'Not connected'} 
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
      {/* TikTok Integration Card (now after WhatsApp) */}
      <Card sx={{ mb: 3, opacity: 0.75, filter: 'grayscale(10%)', cursor: 'not-allowed' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 1, width: 24, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* TikTok SVG icon - fixed bottom cut */}
              <svg viewBox="0 0 48 56" fill="none" style={{ width: 24, height: 32 }}>
                <g>
                  <path d="M41.5 17.5c-3.6 0-6.5-2.9-6.5-6.5V6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#25F4EE"/>
                  <path d="M41.5 19.5c-3.6 0-6.5-2.9-6.5-6.5V8h-4.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-4.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V25.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#FE2C55"/>
                  <path d="M35 13c0 3.6 2.9 6.5 6.5 6.5v-4c-1.9 0-3.5-1.6-3.5-3.5H35z" fill="#25F4EE"/>
                  <path d="M35 11c0 3.6 2.9 6.5 6.5 6.5v-2c-2.5 0-4.5-2-4.5-4.5H35z" fill="#FE2C55"/>
                  <path d="M35 6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8c-3.6 0-6.5-2.9-6.5-6.5V6z" fill="#000"/>
                </g>
              </svg>
            </Box>
            TikTok Integration
            <Box
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                backgroundColor: '#f44336',
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: 'white',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Soon
            </Box>
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="TikTok Username"
                secondary={user?.tiktokUsername || 'Not connected'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="TikTok ID"
                secondary={user?.tiktokId || 'Not connected'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Company"
                secondary={user?.tiktokCompany || 'Not available'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Email"
                secondary={user?.tiktokEmail || 'Not available'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Phone"
                secondary={user?.tiktokPhone || 'Not available'}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card sx={{ border: '2px solid #ff6b6b' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#d63031' }}>
            <Warning sx={{ mr: 1 }} />
            Danger Zone
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Once you delete your account, there is no going back. Please be certain.
          </Typography>
          
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{ borderColor: '#ff6b6b', color: '#d63031' }}
          >
            Delete My Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d63031', display: 'flex', alignItems: 'center' }}>
          <Warning sx={{ mr: 1 }} />
          Delete Account
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
          </Alert>
          
          <Typography variant="body2" color="text.secondary">
            This will delete:
          </Typography>
          
          <List dense sx={{ mt: 1 }}>
            <ListItem>
              <ListItemIcon>
                <Info fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Your user account" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Info fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="All messages and conversations" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Info fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Auto-reply settings" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Info fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="ChatGPT prompt configurations" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Info fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="All connected social media integrations" />
            </ListItem>
          </List>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please type <strong>DELETE</strong> to confirm.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? undefined : <Delete />}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default Settings;
